import React, { useState } from 'react';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { BulkLinkBar, GroupSummary } from './BulkLink';

const AUTO = '__auto';
const gbp = (v) => `£${(parseFloat(v) || 0).toFixed(2)}`;

const StatusBadge = ({ linked, autoLabel, testId }) => {
  if (linked) return <Badge className="bg-emerald-600 hover:bg-emerald-600" data-testid={testId}>Linked</Badge>;
  if (autoLabel) return <Badge variant="secondary" data-testid={testId}>Auto</Badge>;
  return <Badge className="bg-amber-500 hover:bg-amber-500" data-testid={testId}>Not matched</Badge>;
};

export const MappingSection = ({ category, items, targets, links, onChange, onlyUnmatched, qtyTargets, qtyLinks = {} }) => {
  const withQty = !!qtyTargets;
  const cols = withQty ? ['sm:col-span-3', 'sm:col-span-3', 'sm:col-span-2'] : ['sm:col-span-4', 'sm:col-span-4', ''];
  const [selected, setSelected] = useState([]);
  const labelOf = (v) => targets.find(t => t.value === v)?.label;
  const effectiveOf = (i) => links[i.key] || (labelOf(i.auto) ? i.auto : null);
  const rows = items.filter(i => !onlyUnmatched || !effectiveOf(i));
  const toggle = (key) => setSelected(s => (s.includes(key) ? s.filter(k => k !== key) : [...s, key]));
  if (!items.length) {
    return <p className="text-sm text-muted-foreground py-6 text-center" data-testid={`mapping-empty-${category}`}>No names yet - upload a Day End report for this store.</p>;
  }
  return (
    <div className="space-y-3">
      <BulkLinkBar category={category} count={selected.length} targets={targets} onClear={() => setSelected([])}
        onApply={(t) => { selected.forEach(k => onChange(category, k, t)); setSelected([]); }} />
      <GroupSummary category={category} items={items} targets={targets} effectiveOf={effectiveOf} />
      <div className="divide-y rounded-lg border" data-testid={`mapping-section-${category}`}>
        <div className="hidden sm:grid grid-cols-12 gap-3 px-3 py-2 text-xs font-semibold uppercase text-muted-foreground bg-muted/40">
          <span className={cols[0].replace('sm:', '')}>Tick to group · On the till report</span>
          <span className="col-span-2 text-right">Last amount</span>
          <span className={cols[1].replace('sm:', '')}>{withQty ? 'Amount goes to' : 'Fills this field in the system'}</span>
          {withQty && <span className="col-span-2">Quantity goes to</span>}
          <span className="col-span-2 text-right">Status</span>
        </div>
        {rows.map(i => {
          const autoLabel = labelOf(i.auto);
          return (
            <div key={i.key} className="grid grid-cols-2 sm:grid-cols-12 gap-2 sm:gap-3 px-3 py-2 items-center text-sm" data-testid={`mapping-row-${category}-${i.key}`}>
              <label className={`${cols[0]} font-medium break-words flex items-center gap-2 cursor-pointer`}>
                <Checkbox checked={selected.includes(i.key)} onCheckedChange={() => toggle(i.key)} data-testid={`mapping-check-${category}-${i.key}`} />
                {i.name}
              </label>
              <span className="sm:col-span-2 text-right font-mono text-xs">
                {gbp(i.amount)}{i.qty ? <span className="block text-muted-foreground">qty {i.qty}</span> : null}
              </span>
              <div className={`col-span-2 ${cols[1]}`}>
                <Select value={links[i.key] || AUTO} onValueChange={(v) => onChange(category, i.key, v === AUTO ? null : v)}>
                  <SelectTrigger className="h-8 text-xs" data-testid={`mapping-select-${category}-${i.key}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value={AUTO}>{autoLabel ? `Automatic: ${autoLabel}` : 'Automatic: not matched'}</SelectItem>
                    {targets.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {withQty && (
                <div className={`col-span-2 ${cols[2]}`}>
                  <Select value={qtyLinks[i.key] || AUTO} onValueChange={(v) => onChange('shop_qty', i.key, v === AUTO ? null : v)}>
                    <SelectTrigger className="h-8 text-xs" data-testid={`mapping-qty-select-${i.key}`}><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-72">
                      <SelectItem value={AUTO}>{qtyTargets.find(t => t.value === i.auto_qty) ? `Automatic: ${qtyTargets.find(t => t.value === i.auto_qty).label}` : 'Automatic: none'}</SelectItem>
                      {qtyTargets.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <span className="col-span-2 text-right">
                <StatusBadge linked={!!links[i.key]} autoLabel={autoLabel} testId={`mapping-status-${category}-${i.key}`} />
              </span>
            </div>
          );
        })}
        {!rows.length && <p className="text-sm text-muted-foreground py-4 text-center">Everything is matched.</p>}
      </div>
    </div>
  );
};
